/**
 * The band at the top of a session: who the patient is, what brought them in, what the
 * report concluded, and where the session stands — the eval dashboard's `VerdictHeader`
 * chrome, holding the live session's own facts.
 *
 * It sits directly on the page rather than on a card, with the round back control as the only
 * way out that is not the browser's own. Name, age and sex arrive with the opening description,
 * and the working diagnosis, urgency and category only with the report, so each says so while
 * it is missing rather than leaving a gap that reads as a layout bug.
 */

import type { AdminSessionDetail } from "../../api/types";
import { calendarDate, clockTime, EM_DASH, orDash } from "../../lib/format";
import { patientDetails } from "../../lib/patient";
import { deriveUrgencyFromDifferentials, URGENCY_PENDING_TITLE } from "../../lib/urgency";
import Acuity from "../ui/Acuity";
import BackLink from "../ui/BackLink";
import StatusTag from "../ui/StatusTag";

/** A labelled line of prose, with what to say while it is still missing. */
function Fact({ label, text, missing }: { label: string; text: string; missing: string }) {
  return (
    <div>
      <p className="text-caption text-ink-faint">{label}</p>
      <p className={text !== "" ? "text-body-sm text-ink-3" : "text-body-sm text-ink-faint"}>
        {text !== "" ? text : missing}
      </p>
    </div>
  );
}

export default function SessionHeader({ detail }: { detail: AdminSessionDetail }) {
  const { patient, report } = detail;
  const name = patient.name.trim();
  const identity = [patientDetails(patient.age, patient.sex), detail.user.email]
    .filter((part) => part !== "")
    .join(" · ");

  return (
    <header className="flex flex-wrap items-start gap-x-8 gap-y-5 px-2 py-4">
      <BackLink to="/sessions" label="Back to the session list" />

      <div className="flex min-w-0 max-w-[64ch] flex-1 basis-[20rem] flex-col gap-3">
        <div>
          <h1 className="text-title text-ink">{name === "" ? "Unnamed patient" : name}</h1>
          <p className="mt-1.5 text-caption text-ink-faint">{identity}</p>
        </div>
        <Fact
          label="Presenting complaint"
          text={detail.presenting_complaint?.trim() ?? ""}
          missing="Not recorded yet — it is set from the patient's opening description."
        />
        <Fact
          label="Working diagnosis"
          text={report?.working_diagnosis.trim() ?? ""}
          missing={
            report === null
              ? "Set by the report, when the consultation completes."
              : "None recorded."
          }
        />
      </div>

      <dl className="grid shrink-0 grid-cols-[auto_auto] items-center gap-x-6 gap-y-2 text-cell lg:ml-auto">
        <dt className="text-ink-3">Started</dt>
        <dd className="text-ink">
          {calendarDate(detail.created_at)}{" "}
          <span className="text-ink-faint">{clockTime(detail.created_at)}</span>
        </dd>
        <dt className="text-ink-3">Status</dt>
        <dd>
          <StatusTag status={detail.status} />
        </dd>
        <dt className="text-ink-3">Urgency</dt>
        <dd>
          <Acuity
            urgency={report ? deriveUrgencyFromDifferentials(report.working_differentials) : null}
            missingTitle={URGENCY_PENDING_TITLE}
          />
        </dd>
        <dt className="text-ink-3">Category</dt>
        <dd className="text-ink">{report ? orDash(report.category) : EM_DASH}</dd>
      </dl>
    </header>
  );
}
